import axios from 'axios';

const SERVICE_REGISTRY_URL = import.meta.env.VITE_SERVICE_REGISTRY_URL ||
  'https://service-registry-api-yplq6vaegq-uc.a.run.app';

// Create axios instance with default config
const api = axios.create({
  baseURL: SERVICE_REGISTRY_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token if using IAP
api.interceptors.request.use((config) => {
  // IAP will handle auth automatically in production
  // For local dev, you might need to add a token here
  return config;
});

// Service types
export interface Service {
  name: string;
  url: string;
  source: string;
  region: string;
  last_seen: string;
  created: string;
  ready: boolean;
  health?: 'healthy' | 'unhealthy' | 'unknown';
  type?: string;
  description?: string;
  metadata: {
    type?: string;
    description?: string;
    endpoints?: Record<string, string>;
  };
}

export interface AgentPermissions {
  [agentName: string]: string[];
}

export interface PermissionChange {
  agent: string;
  services: string[];
  changes: {
    added: string[];
    removed: string[];
  };
  requestedBy: string;
  type: 'grant' | 'revoke' | 'bulk_update';
}

// API calls
export async function getServices(): Promise<Service[]> {
  const response = await api.get('/services');
  const services = response.data.services;

  // Convert object to array and add health status
  return Object.entries(services).map(([name, service]: [string, any]) => ({
    name,
    ...service,
    health: service.ready ? 'healthy' : 'unhealthy',
    type: service.metadata?.type || 'unknown',
    description: service.metadata?.description || 'No description available',
  }));
}

export async function getAgentPermissions(): Promise<AgentPermissions> {
  const response = await api.get('/agents/permissions');
  return response.data.permissions;
}

export async function getAgentServices(agentName: string): Promise<string[]> {
  const response = await api.get(`/agents/${agentName}/services`);
  return response.data.services;
}

export async function updateAgentPermissions(
  agentName: string,
  services: string[]
): Promise<void> {
  await api.post(`/agents/${agentName}/permissions`, { services });
}

export async function grantPermission(
  agentName: string,
  serviceName: string
): Promise<void> {
  await api.post(`/agents/${agentName}/grant/${serviceName}`);
}

export async function revokePermission(
  agentName: string,
  serviceName: string
): Promise<void> {
  await api.post(`/agents/${agentName}/revoke/${serviceName}`);
}

// Health check
export async function checkHealth(): Promise<boolean> {
  try {
    const response = await api.get('/health');
    return response.data.status === 'healthy';
  } catch {
    return false;
  }
}

// Discovery configuration
export interface DiscoveryConfig {
  prefixes: string[];
  excludePatterns: string[];
  refreshInterval: number;
  lastRefresh?: string;
  autoDiscoverEnabled: boolean;
}

export async function getDiscoveryConfig(): Promise<DiscoveryConfig> {
  const response = await api.get('/discovery/config');
  return response.data.config;
}

export async function updateDiscoveryConfig(config: Partial<DiscoveryConfig>): Promise<void> {
  await api.post('/discovery/config', config);
}

export async function triggerManualRefresh(): Promise<{ refreshed: boolean; servicesFound: number }> {
  const response = await api.post('/discovery/refresh');
  return response.data;
}
